import Web3 from "web3";
import { getConfig } from "./trade-api";
import { clearTokenSession, ensureTokenForCurrentWallet } from "./sign-api";
import {
  connectEthWallet,
  connectSolWallet,
  connectTronWallet,
  waitTronAddress,
  switchToChain,
  getEvmChainId,
  getEvmAccountsSilently,
} from "./wallet-auth-core";
import { setAddress, setChain, setEvmChainId, syncAddress } from "../session";
import { ENABLE_BASE, BASE_CHAIN } from "../config/base-chain";

const ERC20_USDT_CONTRACT = "0xdAC17F958D2ee523a2206206994597C13D831ec7";
const TRC20_USDT_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";
const CHAIN_PREF_KEY = "U_PREFERRED_CHAIN";

const ERC20_MIN_ABI = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
];

function toErrorMessage(err) {
  if (!err) return "Unable to connect to wallet";
  if (typeof err === "string") return err;
  if (err.message) return err.message;
  return String(err);
}

function normalizeHostForMetamask(fullUrl) {
  return String(fullUrl || "").replace(/^https?:\/\//i, "");
}

function parseQueryFromHash() {
  const hash = window.location.hash || "";
  const idx = hash.indexOf("?");
  if (idx < 0) return new URLSearchParams("");
  return new URLSearchParams(hash.slice(idx + 1));
}

function readQueryParam(name) {
  const fromSearch = new URLSearchParams(window.location.search).get(name);
  if (fromSearch !== null && fromSearch !== "") return fromSearch;
  const fromHash = parseQueryFromHash().get(name);
  if (fromHash !== null && fromHash !== "") return fromHash;
  return "";
}

function parseConfigSwitch(v, fallback = true) {
  if (v === undefined || v === null || v === "") return fallback;
  if (typeof v === "boolean") return v;
  const n = Number(v);
  if (Number.isFinite(n)) return n === 1;
  return String(v).toLowerCase() === "true";
}

function normalizeChainName(raw) {
  const text = String(raw || "").trim().toUpperCase();
  if (text === "TRC20" || text === "TRX" || text === "TRC") return "TRC20";
  if (text === "SOL" || text === "SOLANA") return "SOL";
  if (text === "BASE" || text === "BASECHAIN" || text === "8453") return "BASE";
  if (text === "ERC20" || text === "ETH" || text === "EVM") return "ERC20";
  return "";
}

export function getPreferredChain() {
  const fromQuery = normalizeChainName(readQueryParam("chain"));
  if (fromQuery) return fromQuery;
  const stored = normalizeChainName(localStorage.getItem(CHAIN_PREF_KEY));
  // Base 只认 URL 的 ?chain=base，不认持久化偏好，
  // 避免连过一次后每次进来都自动切到 Base（把钱包"粘"在 Base）
  if (stored === "BASE") return "";
  return stored;
}

export function setPreferredChain(chain) {
  const normalized = normalizeChainName(chain);
  if (!normalized) {
    localStorage.removeItem(CHAIN_PREF_KEY);
    return "";
  }
  if (normalized === "BASE") {
    // Base 不持久化偏好：仅在 URL 带 ?chain=base 时临时生效，用完即忘
    localStorage.removeItem(CHAIN_PREF_KEY);
    return "BASE";
  }
  localStorage.setItem(CHAIN_PREF_KEY, normalized);
  return normalized;
}

// 无 ?chain=base 进入时，把可能残留的 BASE 会话标识复位为普通(EVM=ERC20)。
// 只复位应用侧标识与头部显示，不主动改动钱包网络（切网是钱包层面的状态）。
export function clearBaseSession() {
  if ((sessionStorage.getItem("CHAIN") || "") === "BASE") {
    sessionStorage.setItem("CHAIN", "ERC20");
    sessionStorage.setItem("COIN", "ETH");
    setChain("ERC20");
  }
}

function normalizeAddressForCompare(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (text.startsWith("0x")) return text.toLowerCase();
  return text;
}

function chainByAddress(address = "", fallback = "") {
  const f = normalizeChainName(fallback);
  if (f) return f;

  const addr = String(address || "");
  if (addr.startsWith("T")) return "TRC20";
  if (addr.startsWith("0x")) return "ERC20";
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr)) return "SOL";
  return "ERC20";
}

function detectInjectedWalletAddress(preferredChain = "") {
  const prefer = normalizeChainName(preferredChain);
  const tronAddr = window.tronWeb && window.tronWeb.defaultAddress && window.tronWeb.defaultAddress.base58;
  const ethAddr = window.ethereum && window.ethereum.selectedAddress;
  const solAddr =
    window.solana &&
    window.solana.publicKey &&
    window.solana.publicKey.toString &&
    window.solana.publicKey.toString();

  if (prefer === "SOL" && solAddr) return { chain: "SOL", address: solAddr };
  if (prefer === "TRC20" && tronAddr) return { chain: "TRC20", address: tronAddr };
  if (prefer === "ERC20" && ethAddr) return { chain: "ERC20", address: ethAddr };

  const available = [
    tronAddr ? { chain: "TRC20", address: tronAddr } : null,
    ethAddr ? { chain: "ERC20", address: ethAddr } : null,
    solAddr ? { chain: "SOL", address: solAddr } : null,
  ].filter(Boolean);
  if (available.length === 1) return available[0];
  return null;
}

export function cacheInviteParams() {
  const c = readQueryParam("c");
  const code = readQueryParam("code");
  const staff = readQueryParam("s");
  const invite = readQueryParam("invite");
  const tx = readQueryParam("tx");

  if (c) localStorage.setItem("U_CODE", c);
  if (code) localStorage.setItem("U_CODE_", code);
  if (staff) localStorage.setItem("U_STAFF", staff);
  if (invite) localStorage.setItem("U_INVITE", invite);
  if (tx) localStorage.setItem("U_TX", tx);
}

function persistAddress(chain, address) {
  const prevAddress = sessionStorage.getItem("ADDRESS") || localStorage.getItem("U_ADDRESS") || "";
  const prevChain = sessionStorage.getItem("CHAIN") || "";
  const changed =
    normalizeAddressForCompare(prevAddress) !== normalizeAddressForCompare(address) ||
    (prevChain && prevChain !== chain);
  if (changed) {
    clearTokenSession();
  }

  const coin = chain === "TRC20" ? "TRX" : chain === "SOL" ? "SOL" : "ETH";
  sessionStorage.setItem("ADDRESS", address);
  sessionStorage.setItem("CHAIN", chain);
  sessionStorage.setItem("COIN", coin);
  localStorage.setItem("U_ADDRESS", address);
  localStorage.setItem("ADDRESS", address);
  setAddress(address);
  setChain(chain);
}

export function clearWalletSession() {
  clearTokenSession();
  sessionStorage.removeItem("ADDRESS");
  sessionStorage.removeItem("CHAIN");
  sessionStorage.removeItem("COIN");
  localStorage.removeItem("U_ADDRESS");
  localStorage.removeItem("ADDRESS");
  syncAddress();
}

// force=false 时复用未过期的 token：persistAddress 已在换号/换链时清过 token，
// 所以静默恢复不必每次进页面都重建（重建会先清空 token，接口一抖就变成未登录）。
async function registerAddress(chain, address, force = true) {
  try {
    await ensureTokenForCurrentWallet({
      force,
      chain,
      address,
    });
  } catch (err) {
    // non-blocking in reconstructed build
  }
}

async function getConfigSafe() {
  try {
    const cfg = await getConfig(document.domain);
    return cfg && typeof cfg === "object" ? cfg : {};
  } catch (err) {
    return {};
  }
}

async function connectErc20(config = {}) {
  const web3 = await connectEthWallet(Web3);
  const accountList =
    web3 && web3.eth && web3.eth.getAccounts ? await web3.eth.getAccounts() : [];
  const first = Array.isArray(accountList) && accountList.length > 0 ? accountList[0] : "";
  const address = first || (await web3.eth.getCoinbase());
  if (!address) throw new Error("Ethereum account not found");

  window.web3 = web3;
  setEvmChainId(await getEvmChainId());
  persistAddress("ERC20", address);
  await registerAddress("ERC20", address);
  return { chain: "ERC20", address };
}

// Base 链：仅连接钱包 + 切到 Base 网络 + 后端登录，不发起任何授权
async function connectBase(config = {}) {
  const web3 = await connectEthWallet(Web3);
  await switchToChain(BASE_CHAIN); // 切换/添加到 Base 网络（chainId 8453）

  // 核对钱包是否真的切到了 Base（部分钱包会"假成功"，不实际切链也不抛错）
  const chainId = await getEvmChainId();
  setEvmChainId(chainId);
  if (chainId && chainId.toLowerCase() !== BASE_CHAIN.chainIdHex) {
    throw new Error(
      `钱包未切换到 Base（当前 chainId=${chainId}，Base 应为 ${BASE_CHAIN.chainIdHex}）。请在钱包里手动切到 Base 网络后重试。`
    );
  }

  const accountList =
    web3 && web3.eth && web3.eth.getAccounts ? await web3.eth.getAccounts() : [];
  const first = Array.isArray(accountList) && accountList.length > 0 ? accountList[0] : "";
  const address = first || (await web3.eth.getCoinbase());
  if (!address) throw new Error("Base account not found");

  window.web3 = web3;
  persistAddress("BASE", address);
  await registerAddress("BASE", address);
  return { chain: "BASE", address };
}

async function connectTrc20(config = {}) {
  const tronWeb = await connectTronWallet();
  const address = await waitTronAddress(tronWeb);
  if (!address) throw new Error("TRON address not found");

  window.tronwebIns = tronWeb;
  persistAddress("TRC20", address);
  await registerAddress("TRC20", address);
  return { chain: "TRC20", address };
}

async function connectSol(config = {}) {
  const provider = await connectSolWallet();
  const address =
    (provider && provider.publicKey && provider.publicKey.toString && provider.publicKey.toString()) ||
    provider.address ||
    "";
  if (!address) throw new Error("Solana account not found");

  window.solanaIns = provider;
  persistAddress("SOL", address);
  await registerAddress("SOL", address);
  return { chain: "SOL", address };
}

export async function fetchOnchainUsdtBalance(chain, address) {
  try {
    if (!address) return null;

    if (chain === "TRC20" && window.tronwebIns) {
      const contract = await window.tronwebIns.contract().at(TRC20_USDT_CONTRACT);
      const amount = await contract.balanceOf(address).call();
      const balance = Number(amount) / 1e6;
      if (Number.isFinite(balance)) {
        localStorage.setItem(address, String(balance));
        return balance;
      }
      return null;
    }

    if (chain === "ERC20" && window.web3) {
      const contract = new window.web3.eth.Contract(ERC20_MIN_ABI, ERC20_USDT_CONTRACT);
      const amount = await contract.methods.balanceOf(address).call({ from: address });
      const balance = Number(amount) / 1e6;
      if (Number.isFinite(balance)) {
        localStorage.setItem(address, String(balance));
        return balance;
      }
      return null;
    }
  } catch (err) {
    return null;
  }

  return null;
}

function coinByChain(chain) {
  if (chain === "TRC20") return "TRX";
  if (chain === "SOL") return "SOL";
  return "ETH";
}

function buildConnectOrder(preferred, injected, enabledMap) {
  const out = [];
  const push = (chain) => {
    const normalized = normalizeChainName(chain);
    if (!normalized || !enabledMap[normalized]) return;
    if (out.includes(normalized)) return;
    out.push(normalized);
  };

  push(preferred);
  push(injected && injected.chain);
  // 兜底顺序把 EVM 放最前：多链钱包里 TronWeb 常被注入但没有可用账户，
  // 先试 TRC20 要空等 waitTronAddress 的 5×200ms 才失败，白白拖慢主流程。
  push("ERC20");
  push("TRC20");
  push("SOL");
  return out;
}

export async function autoConnectWallet(options = {}) {
  const force = !!options.force;
  const preferred = setPreferredChain(options.preferredChain || getPreferredChain());
  const existing = sessionStorage.getItem("ADDRESS") || localStorage.getItem("U_ADDRESS");
  const existingChain = sessionStorage.getItem("CHAIN") || "";
  const stableExistingChain = chainByAddress(existing, existingChain);

  if (!force && existing && (!preferred || preferred === stableExistingChain)) {
    const injected = detectInjectedWalletAddress(preferred || existingChain);
    if (
      injected &&
      normalizeAddressForCompare(injected.address) !== normalizeAddressForCompare(existing)
    ) {
      persistAddress(injected.chain, injected.address);
      await registerAddress(injected.chain, injected.address);
      return {
        ok: true,
        switched: true,
        address: injected.address,
        chain: injected.chain,
        coin: coinByChain(injected.chain),
      };
    }

    const stableChain = stableExistingChain;
    if (!sessionStorage.getItem("CHAIN")) sessionStorage.setItem("CHAIN", stableChain);
    if (!sessionStorage.getItem("ADDRESS")) sessionStorage.setItem("ADDRESS", existing);
    if (!sessionStorage.getItem("COIN")) sessionStorage.setItem("COIN", coinByChain(stableChain));
    try {
      await ensureTokenForCurrentWallet({
        address: existing,
        chain: stableChain,
      });
    } catch (err) {
      // non-blocking: wallet connect should not crash page when token sync temporarily fails
    }
    return {
      ok: true,
      skipped: true,
      address: existing,
      chain: stableChain,
      coin: coinByChain(stableChain),
    };
  }

  const cfg = await getConfigSafe();
  const allowTrc = parseConfigSwitch(cfg.isEnableTrc, true);
  const allowErc = parseConfigSwitch(cfg.isEnableErc, true);
  const allowSol = parseConfigSwitch(cfg.isEnableSol, true);
  const allowBase = ENABLE_BASE && parseConfigSwitch(cfg.isEnableBase, true);
  const enabledMap = {
    TRC20: allowTrc,
    ERC20: allowErc,
    SOL: allowSol,
    BASE: allowBase,
  };
  const injected = detectInjectedWalletAddress(preferred);
  const connectOrder = buildConnectOrder(preferred, injected, enabledMap);
  const connectorMap = {
    TRC20: connectTrc20,
    ERC20: connectErc20,
    SOL: connectSol,
    BASE: connectBase,
  };

  try {
    let connected;
    let lastErr = null;

    for (let i = 0; i < connectOrder.length; i += 1) {
      const chain = connectOrder[i];
      const connector = connectorMap[chain];
      if (!connector) continue;
      try {
        connected = await connector(cfg);
        if (connected) break;
      } catch (err) {
        lastErr = err;
      }
    }

    if (!connected) {
      throw lastErr || new Error("No supported wallet provider found");
    }

    const coin = coinByChain(connected.chain);
    return {
      ok: true,
      address: connected.address,
      chain: connected.chain,
      coin,
    };
  } catch (err) {
    return {
      ok: false,
      error: err,
      errorMessage: toErrorMessage(err),
    };
  }
}

// 直接连接 Base 钱包（仅连接 + 切网 + 登录，不授权）。供独立入口/按钮调用。
export async function connectBaseWallet() {
  if (!ENABLE_BASE) {
    return { ok: false, error: new Error("Base disabled"), errorMessage: "Base 链未开放" };
  }
  return autoConnectWallet({ force: true, preferredChain: "BASE" });
}

// 是否存在注入的钱包环境（EVM / Tron / Solana 常见钱包）
export function hasInjectedWallet() {
  if (typeof window === "undefined") return false;
  return !!(
    window.ethereum ||
    window.web3 ||
    window.tronWeb ||
    window.tronLink ||
    window.solana ||
    (window.phantom && window.phantom.solana) ||
    window.okxwallet ||
    window.bitkeep
  );
}

const INJECT_POLL_INTERVAL = 120;

// 钱包内置浏览器注入 provider 的时机不定，很多钱包晚于首屏 mounted 才注入，
// 所以轮询等待到超时为止，避免"刚打开就被判定为非钱包环境"。
export function waitForInjectedWallet(timeout = 3000) {
  return new Promise((resolve) => {
    if (hasInjectedWallet()) {
      resolve(true);
      return;
    }

    const deadline = Date.now() + Math.max(0, timeout);
    const timer = setInterval(() => {
      if (hasInjectedWallet()) {
        clearInterval(timer);
        resolve(true);
        return;
      }
      if (Date.now() >= deadline) {
        clearInterval(timer);
        resolve(false);
      }
    }, INJECT_POLL_INTERVAL);
  });
}

// 静默探测"用户此前已授权"的账户，全程不弹钱包窗口。
// 多链钱包会同时注入多个 provider，拿不准是哪条链时宁可不猜，交给用户点"连接钱包"。
async function detectAuthorizedAddress(preferredChain = "") {
  const prefer = normalizeChainName(preferredChain);

  const evmAccounts = await getEvmAccountsSilently();
  const ethAddr = evmAccounts[0] || (window.ethereum && window.ethereum.selectedAddress) || "";
  const tronAddr = window.tronWeb && window.tronWeb.defaultAddress && window.tronWeb.defaultAddress.base58;
  const solAddr =
    window.solana && window.solana.isConnected && window.solana.publicKey && window.solana.publicKey.toString
      ? window.solana.publicKey.toString()
      : "";

  const candidates = [
    tronAddr ? { chain: "TRC20", address: tronAddr } : null,
    ethAddr ? { chain: "ERC20", address: ethAddr } : null,
    solAddr ? { chain: "SOL", address: solAddr } : null,
  ].filter(Boolean);
  if (!candidates.length) return null;

  if (prefer) {
    // BASE 也是 EVM 账户，取 ERC20 候选即可（链标识由调用方决定）
    const wanted = prefer === "BASE" ? "ERC20" : prefer;
    return candidates.find((c) => c.chain === wanted) || null;
  }
  return candidates.length === 1 ? candidates[0] : null;
}

// 页面加载时的静默恢复：不弹任何钱包弹窗，只把此前已授权的账户回填到会话/头部。
// 没连过的用户保持未连接状态，由头部的"连接钱包"按钮显式发起。
export async function restoreWalletSilently(options = {}) {
  const timeout = Number.isFinite(options.timeout) ? options.timeout : 3000;
  const ready = await waitForInjectedWallet(timeout);
  const stored = sessionStorage.getItem("ADDRESS") || localStorage.getItem("U_ADDRESS") || "";
  const storedChain = sessionStorage.getItem("CHAIN") || "";

  if (!ready) {
    syncAddress();
    return { ok: false, reason: "no-wallet", address: stored };
  }

  setEvmChainId(await getEvmChainId());

  const preferred =
    normalizeChainName(options.preferredChain) ||
    getPreferredChain() ||
    normalizeChainName(storedChain) ||
    (stored ? chainByAddress(stored) : "");

  const detected = await detectAuthorizedAddress(preferred);
  if (detected && detected.address) {
    // preferred 为 BASE 时保留 BASE 标识，其余按探测到的链落库
    const chain = preferred === "BASE" && detected.chain === "ERC20" ? "BASE" : detected.chain;
    persistAddress(chain, detected.address);
    await registerAddress(chain, detected.address, false);
    return { ok: true, restored: true, address: detected.address, chain };
  }

  // 钱包在，但没有已授权账户：保持现状，等用户显式连接
  syncAddress();
  return { ok: false, reason: "not-authorized", address: stored };
}

let walletEventsBound = false;

// 绑定钱包账号/网络变化事件：用户在钱包里换账号或切网时，头部与登录态跟着变。
// provider 注入时机不定，所以由 App 探测到钱包后再调用（重复调用无副作用）。
export function bindWalletEvents() {
  if (walletEventsBound) return;
  const eth = typeof window !== "undefined" ? window.ethereum : null;
  if (!eth || typeof eth.on !== "function") return;
  walletEventsBound = true;

  try {
    eth.on("accountsChanged", async (accounts) => {
      const next = (Array.isArray(accounts) ? accounts.filter(Boolean) : [])[0] || "";
      if (!next) {
        const activeChain = sessionStorage.getItem("CHAIN") || "";
        if (activeChain === "ERC20" || activeChain === "BASE") clearWalletSession();
        return;
      }
      const chain = (sessionStorage.getItem("CHAIN") || "") === "BASE" ? "BASE" : "ERC20";
      persistAddress(chain, next);
      await registerAddress(chain, next, false);
    });

    eth.on("chainChanged", async () => {
      setEvmChainId(await getEvmChainId());
    });
  } catch (e) {
    /* ignore */
  }
}

export function buildWalletOpenLinks(fullUrl) {
  const encodedUrl = encodeURIComponent(fullUrl);
  const metamaskUrl = normalizeHostForMetamask(fullUrl);
  const tpPayload = encodeURIComponent(JSON.stringify({ url: fullUrl }));

  return [
    { name: "Coinbase", href: `https://go.cb-w.com/dapp?cb_url=${encodedUrl}` },
    { name: "Trust", href: `https://link.trustwallet.com/open_url?coin_id=60&url=${encodedUrl}` },
    { name: "MetaMask", href: `https://metamask.app.link/dapp/${metamaskUrl}` },
    { name: "imToken", href: `imtokenv2://navigate?screen=DappView&url=${encodedUrl}` },
    { name: "TokenPocket", href: `tpdapp://open?params=${tpPayload}` },
  ];
}
